/**
 * One upload operation per picked image (M10, mobile assessment §G M10).
 *
 * The upload functions store a file once per operation id
 * (supabase/functions/_shared/uploadOnce.ts): a retry with the same id and
 * the same bytes gets the original success back instead of a second copy.
 * For that, a retry must send the SAME id and the SAME bytes, and the bytes
 * are what metadata stripping produced, which is not guaranteed to be
 * byte-identical when run twice. So both are kept until the upload is
 * acknowledged.
 *
 * Keyed by the destination and the image's own CONTENT, not by the File
 * object (Codex on #1121): after a lost answer the screens clear their file
 * input, and picking the same image again yields a new File with the same
 * bytes. That re-pick must be recognised as the retry it is.
 *
 * Held in memory only, at most MAX_PENDING entries. The durable outbox that
 * survives an app restart is later M10 work; this is what it will build on.
 */

interface PendingUpload {
  operationId: string;
  cleaned: File;
}

const MAX_PENDING = 10;
const pending = new Map<string, PendingUpload>();
const keyOfFile = new WeakMap<File, string>();

async function contentKey(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  const hex = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
  return `${file.type}:${hex}`;
}

/**
 * The operation id and cleaned bytes for uploading `file` to `destination`
 * (e.g. a booking and photo kind). The same image to the same destination
 * reuses them while the upload is unacknowledged; anything else starts a
 * new operation.
 */
export async function prepareUpload(
  file: File,
  destination: string,
  clean: (file: File) => Promise<File>,
): Promise<{ operationId: string; cleaned: File }> {
  const key = `${destination}|${await contentKey(file)}`;
  keyOfFile.set(file, key);
  const existing = pending.get(key);
  if (existing) return existing;
  const next = { operationId: crypto.randomUUID(), cleaned: await clean(file) };
  pending.set(key, next);
  while (pending.size > MAX_PENDING) pending.delete(pending.keys().next().value as string);
  return next;
}

/**
 * The server answered for good: success, or a refusal that a retry would
 * not change. The next upload of this image is a new operation.
 */
export function settleUpload(file: File): void {
  const key = keyOfFile.get(file);
  if (key) pending.delete(key);
}

/**
 * Whether `file` still has an unacknowledged operation: its last upload was
 * lost or failed on the server's side, so a retry must resend it as it is.
 */
export function isUploadPending(file: File): boolean {
  const key = keyOfFile.get(file);
  return key !== undefined && pending.has(key);
}

/**
 * Whether an HTTP status is a definite answer to the upload, after which the
 * next upload of the image is a new operation. A failure on the server's side
 * (5xx) is not, and neither is throttling (429, the per-user ceiling in
 * M11): the server read nothing, so a later retry must still send the same
 * id, in case an earlier attempt's answer was lost after it was stored.
 */
export function isDefiniteAnswer(status: number | undefined): boolean {
  return status !== undefined && status !== 0 && status < 500 && status !== 429;
}

/** HTTP status and body of a functions.invoke error. */
export async function invokeErrorDetail(error: unknown): Promise<{ status?: number; message?: string; code?: string }> {
  const context = (error as { context?: Response }).context;
  const status = context?.status;
  try {
    const body = (await context?.clone().json()) as { error?: string; code?: string } | undefined;
    return { status, message: body?.error, code: body?.code };
  } catch {
    return { status };
  }
}

/** Shown when an upload id was already used for different bytes (KB001). */
export const UPLOAD_ALREADY_USED_MESSAGE = 'That file could not be saved as sent. Please choose it again.';
